import React, { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '../../../../ui/components/Common/Buttons/Button';

const OverviewTab = () => {
  const [timeframe, setTimeframe] = useState('Monthly');

  const dailyData = [
    { name: 'Mon', hierTalent: 400, candiBank: 240, assessments: 180 },
    { name: 'Tue', hierTalent: 300, candiBank: 139, assessments: 220 },
    { name: 'Wed', hierTalent: 200, candiBank: 980, assessments: 290 },
    { name: 'Thu', hierTalent: 278, candiBank: 390, assessments: 200 },
    { name: 'Fri', hierTalent: 189, candiBank: 480, assessments: 218 },
    { name: 'Sat', hierTalent: 239, candiBank: 380, assessments: 250 },
    { name: 'Sun', hierTalent: 349, candiBank: 430, assessments: 210 },
  ];

  const monthlyData = [
    { name: 'Jan', hierTalent: 4000, candiBank: 2400, assessments: 2400 },
    { name: 'Feb', hierTalent: 3000, candiBank: 1398, assessments: 2210 },
    { name: 'Mar', hierTalent: 2000, candiBank: 9800, assessments: 2290 },
    { name: 'Apr', hierTalent: 2780, candiBank: 3908, assessments: 2000 },
    { name: 'May', hierTalent: 1890, candiBank: 4800, assessments: 2181 },
    { name: 'Jun', hierTalent: 2390, candiBank: 3800, assessments: 2500 },
  ];

  const yearlyData = [
    { name: '2021', hierTalent: 35000, candiBank: 24000, assessments: 21000 },
    { name: '2022', hierTalent: 45000, candiBank: 32000, assessments: 28000 },
    { name: '2023', hierTalent: 62000, candiBank: 48000, assessments: 39000 },
    { name: '2024', hierTalent: 78000, candiBank: 54000, assessments: 45000 },
  ];

  const joCreationData = [
    { month: 'Jan', count: 12 },
    { month: 'Feb', count: 18 },
    { month: 'Mar', count: 15 },
    { month: 'Apr', count: 22 },
    { month: 'May', count: 30 },
    { month: 'Jun', count: 25 },
  ];

  const getChartData = () => {
    if (timeframe === 'Daily') return dailyData;
    if (timeframe === 'Yearly') return yearlyData;
    return monthlyData;
  };

  const InsightCard = ({ title, metrics, color }: { title: string, metrics: { label: string, value: string | number }[], color: string }) => (
    <div className='bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative'>
      <div className={`absolute top-0 right-0 w-32 h-32 bg-${color}-500/5 rounded-full -mr-16 -mt-16 blur-2xl group-hover:scale-150 transition-transform duration-700`} />
      <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 relative z-10'>{ title }</h3>
      <div className='space-y-4 relative z-10'>
        { metrics.map((m, i) => (
          <div key={i} className='flex justify-between items-end'>
            <span className='text-xs font-bold text-slate-500 tracking-tight'>{ m.label }</span>
            <span className={'text-sm font-black text-slate-800 tracking-tight'}>{ m.value }</span>
          </div>
        )) }
      </div>
      <div className='mt-6 pt-6 border-t border-slate-50'>
        <div className={'h-1.5 w-full bg-slate-50 rounded-full overflow-hidden'}>
          <div className={`h-full bg-${color}-500/50 rounded-full w-2/3`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className='grid grid-cols-1 lg:grid-cols-12 gap-8'>
      { /* Main Content Column */ }
      <div className='lg:col-span-8 space-y-8'>
        { /* Usage Analytics Chart */ }
        <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8'>
          <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8'>
            <div>
              <h2 className='text-lg font-black text-slate-800 tracking-tight'>Usage Analytics</h2>
              <p className='text-xs text-slate-400 font-bold uppercase tracking-widest mt-1'>Resource consumption across active services</p>
            </div>
            <div className='flex bg-slate-50 p-1 rounded-2xl border border-slate-100'>
              { ['Daily', 'Monthly', 'Yearly'].map((t) => (
                <button
                    key={t}
                    onClick={() => setTimeframe(t)}
                    className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${timeframe === t ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'
                    }`}
                >
                  { t }
                </button>
              )) }
            </div>
          </div>

          <div className='h-[350px] w-full'>
            <ResponsiveContainer width='100%' height='100%'>
              <AreaChart data={getChartData()}>
                <defs>
                  <linearGradient id='colorHT' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#10b981' stopOpacity={0.1} />
                    <stop offset='95%' stopColor='#10b981' stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id='colorCB' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#3b82f6' stopOpacity={0.1} />
                    <stop offset='95%' stopColor='#3b82f6' stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id='colorAS' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#f59e0b' stopOpacity={0.1} />
                    <stop offset='95%' stopColor='#f59e0b' stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#f1f5f9' />
                <XAxis
                    dataKey='name'
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                    dy={10}
                />
                <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                />
                <Tooltip
                    contentStyle={{
                      borderRadius: '16px',
                      border: 'none',
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                />
                <Legend iconType='circle' wrapperStyle={{ paddingTop: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }} />
                <Area
                    type='monotone'
                    dataKey='hierTalent'
                    name='HierTalent Base'
                    stroke='#10b981'
                    strokeWidth={3}
                    fillOpacity={1}
                    fill='url(#colorHT)'
                />
                <Area
                    type='monotone'
                    dataKey='candiBank'
                    name='CandiBank'
                    stroke='#3b82f6'
                    strokeWidth={3}
                    fillOpacity={1}
                    fill='url(#colorCB)'
                />
                <Area
                    type='monotone'
                    dataKey='assessments'
                    name='Auto Assessments'
                    stroke='#f59e0b'
                    strokeWidth={3}
                    fillOpacity={1}
                    fill='url(#colorAS)'
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        { /* Operational Insights Section */ }
        <div className='space-y-6'>
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-xl font-black text-slate-800 tracking-tight'>Operational Insights</h2>
              <p className='text-xs text-slate-400 font-bold uppercase tracking-widest mt-1'>Detailed performance metrics across hiring funnels</p>
            </div>
            <div className='flex gap-2'>
              <button className='px-4 py-2 bg-white border border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-emerald-600 transition-colors'>Export Report</button>
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            { /* JOs Created Per Month */ }
            <div className='bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm md:col-span-2'>
              <div className='flex items-center justify-between mb-8'>
                <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>Job Openings Created (Last 6 Months)</h3>
                <div className='text-2xl font-black text-emerald-600'>115 <span className='text-[10px] text-slate-300'>TOTAL</span></div>
              </div>
              <div className='h-48 w-full'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart data={joCreationData}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#f8fafc' />
                    <XAxis dataKey='month' axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }} />
                    <Tooltip
                        cursor={{ fill: '#f8fafc' }}
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Bar dataKey='count' radius={[6, 6, 0, 0]}>
                      { joCreationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === joCreationData.length - 1 ? '#10b981' : '#e2e8f0'} />
                      )) }
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <InsightCard
                title='Applicants per JO'
                metrics={[
                  { label: 'Minimum', value: 12 },
                  { label: 'Average', value: 48 },
                  { label: 'Maximum', value: 154 },
                ]}
                color='blue'
            />

            <InsightCard
                title='Auto-Assessments (Assigned)'
                metrics={[
                  { label: 'Minimum', value: 8 },
                  { label: 'Average', value: 32 },
                  { label: 'Maximum', value: 98 },
                ]}
                color='amber'
            />

            <InsightCard
                title='Auto-Assessments (Completed)'
                metrics={[
                  { label: 'Minimum', value: 5 },
                  { label: 'Average', value: 24 },
                  { label: 'Maximum', value: 76 },
                ]}
                color='emerald'
            />

            <InsightCard
                title='SmartInterviews (Invited)'
                metrics={[
                  { label: 'Minimum', value: 2 },
                  { label: 'Average', value: 12 },
                  { label: 'Maximum', value: 34 },
                ]}
                color='rose'
            />

            <InsightCard
                title='SmartInterviews (Completed)'
                metrics={[
                  { label: 'Minimum', value: 1 },
                  { label: 'Average', value: 9 },
                  { label: 'Maximum', value: 28 },
                ]}
                color='indigo'
            />
          </div>
        </div>

        { /* Metrics Grid */ }
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          { [
            { label: 'Allocated Licenses', value: '2,500', change: '+12%', trend: 'up', color: 'emerald', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' },
            { label: 'Monthly Active Users', value: '2,184', change: '+5.2%', trend: 'up', color: 'blue', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
            { label: 'API Consumption', value: '84.2%', change: '-2.1%', trend: 'down', color: 'amber', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
          ].map((metric, i) => (
            <div key={i} className='bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-all group'>
              <div className='flex items-center justify-between mb-4'>
                <div className={`w-10 h-10 rounded-2xl bg-${metric.color}-50 flex items-center justify-center text-${metric.color}-600 group-hover:scale-110 transition-transform`}>
                  <svg className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d={metric.icon} />
                  </svg>
                </div>
                <span className={`text-[10px] font-black ${metric.trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                  { metric.change }
                </span>
              </div>
              <h3 className='text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-1'>{ metric.label }</h3>
              <p className='text-2xl font-black text-slate-800 tracking-tight'>{ metric.value }</p>
            </div>
          )) }
        </div>

        { /* Contact Information & General Details */ }
        <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden'>
          <div className='p-8 border-b border-slate-50 flex items-center justify-between'>
            <h2 className='text-lg font-black text-slate-800 tracking-tight'>Contact Information</h2>
            <Button label='Edit' variant='link' className='text-emerald-600 font-bold' />
          </div>
          <div className='p-8 grid grid-cols-1 md:grid-cols-2 gap-10'>
            <div className='space-y-6'>
              <div>
                <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3'>Primary Administrator</p>
                <div className='flex items-center gap-3'>
                  <div className='w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm'>JD</div>
                  <div>
                    <h4 className='text-sm font-bold text-slate-800'>Jane Doe</h4>
                    <p className='text-xs text-slate-500 font-medium tracking-tight'>jane.doe@acmeglobal.com</p>
                  </div>
                </div>
              </div>
              <div>
                <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2'>Corporate Address</p>
                <p className='text-sm text-slate-600 font-semibold leading-relaxed'>
                  101 Innovation Way, Suite 500<br />San Francisco, CA 94103, USA
                </p>
              </div>
            </div>
            <div className='space-y-6'>
              <div>
                <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2'>Billing Email</p>
                <p className='text-sm text-emerald-600 font-bold'>accounts@acmeglobal.com</p>
                <p className='text-xs text-slate-400 font-medium'>+1 (555) 902-1231</p>
              </div>
            </div>
          </div>
        </div>

        { /* License Distribution */ }
        <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8'>
          <div className='flex items-center justify-between mb-8'>
            <h2 className='text-lg font-black text-slate-800 tracking-tight'>License Distribution</h2>
            <svg className='w-5 h-5 text-slate-300' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          </div>
          <div className='space-y-8'>
            { [
              { label: 'Productivity Suite (Enterprise)', used: '1,850', total: '2,000', color: 'emerald' },
              { label: 'Storage Add-on (5TB)', used: '3.2TB', total: '5.0TB', color: 'blue' },
              { label: 'Premium Support', status: 'Active', color: 'amber' },
            ].map((item, i) => (
              <div key={i}>
                <div className='flex justify-between items-end mb-2.5'>
                  <span className='text-xs font-bold text-slate-600 tracking-tight'>{ item.label }</span>
                  <span className='text-[10px] font-black text-slate-800 uppercase tracking-widest'>
                    { item.total ? `${item.used} / ${item.total}` : item.status }
                  </span>
                </div>
                { item.total ? (
                  <div className='h-2 w-full bg-slate-100 rounded-full overflow-hidden'>
                    <div
                        className={`h-full bg-${item.color}-500 rounded-full`}
                        style={{ width: `${(parseFloat(item.used.replace(',', '')) / parseFloat(item.total.replace(',', ''))) * 100}%` }}
                    />
                  </div>
                ) : (
                  <div className='h-2 w-full bg-slate-100 rounded-full overflow-hidden'>
                    <div className={`h-full bg-${item.color}-500 rounded-full w-full`} />
                  </div>
                ) }
              </div>
            )) }
          </div>
        </div>
      </div>

      { /* Sidebar Column */ }
      <div className='lg:col-span-4 space-y-8'>
        { /* Subscription Status */ }
        <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8'>
          <h3 className='text-sm font-black text-slate-800 tracking-tight mb-8'>Subscription Status</h3>
          <div className='space-y-6'>
            <div className='flex items-start gap-4 p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100'>
              <div className='w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600'>
                <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M5 13l4 4L19 7' />
                </svg>
              </div>
              <div>
                <p className='text-xs font-bold text-slate-800'>Status: Active</p>
                <p className='text-[10px] text-slate-400 font-medium'>Renewal: Oct 12, 2024</p>
              </div>
            </div>
            <div className='flex items-start gap-4 p-4 rounded-2xl bg-blue-50/50 border border-blue-100'>
              <div className='w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600'>
                <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
              </div>
              <div>
                <p className='text-xs font-bold text-slate-800'>Annual Billing</p>
                <p className='text-[10px] text-slate-400 font-medium'>$12,500.00 / year</p>
              </div>
            </div>
            <Button label='View Billing History' variant='secondary' className='w-full rounded-2xl py-3 font-bold text-emerald-600 border-emerald-200 hover:bg-emerald-50/50' />
          </div>
        </div>

        { /* Branding Guidelines */ }
        <div className='bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8'>
          <h3 className='text-sm font-black text-slate-800 tracking-tight mb-8'>Branding Guidelines</h3>
          <div className='space-y-6'>
            <div>
              <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3'>Primary Color</p>
              <div className='flex items-center gap-3'>
                <div className='w-12 h-8 rounded-lg bg-[#00A651] shadow-inner' />
                <span className='text-xs font-bold text-slate-700 tracking-widest uppercase'>#00A651</span>
              </div>
            </div>
            <div>
              <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3'>Logo (Dark Theme)</p>
              <div className='w-full h-24 bg-slate-900 rounded-2xl flex items-center justify-center p-4'>
                <img src='https://via.placeholder.com/120x40' alt='Logo mockup' className='filter invert' />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
